import React from "react";
import PropTypes from "prop-types";
import HoverIconButton from "components/HoverIconButton";
import Callout from "components/Callout";
import { useFormikContext } from "formik";
import { SCHEDULE_TYPE } from "utils/constants/price";
import isEmpty from "lodash/isEmpty";
import { getFieldPath, getFormCrudMode } from "utils/form";
import get from "lodash/get";
import ScheduleField from "components/ScheduleField";
import BillingPeriodField from "components/BillingPeriodField";
import MetadataField from "components/MetadataField";
import AmountField from "components/AmountField";
import { FORM_MODE } from "utils/constants/form";
import ActiveField from "components/ActiveField";
import {
  TOOLTIP_POSITION,
  BUTTON_CLASS_CONTEXT,
  UI_THEME
} from "utils/constants/ui";
import Button from "components/Form/fields/Button";
import TextareaController from "components/TextareaController";
import CopyCode from "components/CopyCode";
import SegmentedControl from "components/SegmentedControl";
import { TAX_BEHAVIOR_OPTIONS } from "utils/constants/taxRate";
import { STRIPE_FEATURES } from "utils/constants/stripe";
import { hasSpecifiedTaxBehavior } from "utils/taxRate";
import { useStripeFeature } from "utils/hooks/stripe";

const PriceForm = ({
  pathPrefix = "",
  arrayHelpers,
  priceIx,
  navToProductPriceCreate,
  priceHeaderPrefix,
  innerAmountRef
}) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched,
    initialValues
  } = useFormikContext();

  const formattedPricePath = getFieldPath(pathPrefix, `formattedPrice`);
  const recurringPath = getFieldPath(pathPrefix, `recurring`);
  const recurringIntervalPath = getFieldPath(pathPrefix, `recurring.interval`);
  const recurringIntervalCountPath = getFieldPath(
    pathPrefix,
    `recurring.interval_count`
  );
  const currencyPath = getFieldPath(pathPrefix, `currency`);
  const activePath = getFieldPath(pathPrefix, `active`);
  const idPath = getFieldPath(pathPrefix, `id`);
  const metadataPath = getFieldPath(pathPrefix, `metadata`);
  const nicknamePath = getFieldPath(pathPrefix, `nickname`);
  const taxBehaviorPath = getFieldPath(pathPrefix, `tax_behavior`);

  const formattedPrice = get(values, formattedPricePath);
  const recurringInterval = get(values, recurringIntervalPath);
  const currency = get(values, currencyPath);
  const active = get(values, activePath);
  const idValue = get(values, idPath);
  const taxBehavior = get(values, taxBehaviorPath);
  const initialTaxBehavior = get(initialValues, taxBehaviorPath);

  const taxEnabled = useStripeFeature({ feature: STRIPE_FEATURES.TAX });

  const taxBehaviorTouched = get(touched, taxBehaviorPath);

  const formMode = getFormCrudMode(idValue);
  const EDIT_MODE = formMode === FORM_MODE.EDIT;

  const scheduleValue = isEmpty(recurringInterval)
    ? SCHEDULE_TYPE.ONE_TIME
    : SCHEDULE_TYPE.RECURRING;

  let header;
  if (arrayHelpers) {
    const count = priceIx + 1;
    const priceHeader = (header = priceHeaderPrefix
      ? `${priceHeaderPrefix} ${count}`
      : `New Price ${priceIx + 1}`);
    header = (
      <div className="w-100 flex flex-row justify-between items-center">
        <div className="f6 fw5">{priceHeader}</div>
        <HoverIconButton
          type="delete"
          tooltip="Delete"
          tooltipPosition={TOOLTIP_POSITION.RIGHT}
          onClick={() => {
            arrayHelpers.remove(priceIx);
          }}
        />
      </div>
    );
  }
  let fields = [
    <AmountField
      innerRef={innerAmountRef}
      disabled={EDIT_MODE}
      withCalculator={!EDIT_MODE}
      value={formattedPrice}
      name={formattedPricePath}
      currency={currency}
      currencyPath={currencyPath}
      recurringIntervalPath={recurringIntervalPath}
      recurringInterval={recurringInterval}
    />
  ];
  if (EDIT_MODE && navToProductPriceCreate) {
    fields.push(
      <div className="flex flex-column">
        <Callout
          copy={`Existing price amounts can't be edited, but you can create a new price.`}
          customClasses={{
            container:
              "flex flex-column pa2 ba br2 items-start b--black-10 mv2 mv0-ns",
            copy: "f6 lh-copy pr2 pb2 w-100"
          }}
        >
          <Button
            theme="slim"
            classes={{
              contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
            }}
            onClick={navToProductPriceCreate}
            copy="New"
          />
        </Callout>
      </div>
    );
  }

  fields.push(
    <ScheduleField
      name={recurringPath}
      intervalName={recurringIntervalPath}
      value={scheduleValue}
      disabled={EDIT_MODE}
    />
  );

  if (!isEmpty(recurringInterval)) {
    fields.push(
      <BillingPeriodField
        disabled={EDIT_MODE}
        intervalName={recurringIntervalPath}
        intervalCountName={recurringIntervalCountPath}
        value={recurringInterval}
      />
    );
  }

  fields.push(
    <TextareaController
      name={"nickname"}
      label={"Nickname"}
      key={nicknamePath}
      rootPath={pathPrefix}
      fieldKey={"nickname"}
      minRows={1}
    />
  );

  if (taxEnabled) {
    const cantUpdateSpecifiedTaxBehavior =
      EDIT_MODE && hasSpecifiedTaxBehavior(initialTaxBehavior);
    const taxBehaviorProps = {
      options: TAX_BEHAVIOR_OPTIONS,
      disabled: cantUpdateSpecifiedTaxBehavior,
      theme: UI_THEME.SLIM,
      value: taxBehavior,
      onClick: (_, selection) => {
        if (!taxBehaviorTouched) {
          setFieldTouched(taxBehaviorPath, true);
        }
        setFieldValue(taxBehaviorPath, selection);
      }
    };

    fields.push(
      <div className="w-100 pt3">
        <SegmentedControl {...taxBehaviorProps} />
      </div>
    );
  }

  if (EDIT_MODE) {
    fields = fields.concat([
      <CopyCode
        label={"ID"}
        value={idValue}
        tooltip="Copy price id"
        toast={"Copied price id to clipboard"}
      />,
      <ActiveField name={activePath} value={active} />,
      <MetadataField name={metadataPath} />
    ]);
  }

  return (
    <div className="flex flex-column w-100">
      {header}
      {fields.map((node, nodeIx) => (
        <div key={nodeIx} className="pt3 mt2">
          {node}
        </div>
      ))}
    </div>
  );
};

PriceForm.propTypes = {
  name: PropTypes.string,
  pathPrefix: PropTypes.string,
  product: PropTypes.object
};

export default PriceForm;
